import {
	createContext,
	ReactNode,
	useContext,
	useLayoutEffect,
	useState,
} from 'react';

interface connectionProps {
	type: 'multiple' | 'single';
	functions?: (
		| 'edit_price'
		| 'edit_stock'
		| 'cart'
		| 'offers'
		| 'offers_moved'
		| 'purchase_order'
	)[];
	business?: businessProps;
}

interface businessProps {
	cnpj?: string;
	fantasia?: string;
	saleId?: string;
}
interface authProps {
	token?: string;
}
interface appContextProps {
	updateToken: (token?: string | null) => void;
	updateAuthId: (token: string) => void;
	signOut: () => void;
	updateTypeConnection: (
		type: connectionProps['type'],
		functions?: connectionProps['functions'],
	) => void;
	updateBusiness: (business: businessProps) => void;
	connection: connectionProps;
	auth: authProps;
	isTV: boolean;
}
const appContext = createContext<appContextProps>({} as appContextProps);

export const useApp = () => useContext(appContext);

function getInitialAuth(): authProps {
	if (typeof localStorage === 'undefined') return {};

	const token = localStorage.getItem('token');

	return token ? { token } : {};
}

function getIsTVFromUserAgent() {
	if (typeof navigator === 'undefined') return false;

	const userAgent = navigator.userAgent.toLowerCase();
	const tvKeywords = [
		'smarttv',
		'hbbtv',
		'netcast',
		'viera',
		'aquos',
		'appletv',
		'googletv',
		'pov_tv',
		'tizen',
		'webos',
		'nettv',
		'roku',
		'hisense',
		'tv',
	];

	return tvKeywords.some((keyword) => userAgent.includes(keyword));
}

export function AppProvider({ children }: { children: ReactNode }) {
	const [connection, setConnection] = useState<connectionProps>(
		{} as connectionProps,
	);
	const [auth, setAuth] = useState<authProps>(getInitialAuth);
	const [isTV] = useState(getIsTVFromUserAgent);

	const updateToken = (token?: string | null) => {
		if (token) {
			const previous = localStorage.getItem('token');
			if (previous && previous !== token) {
				// Outra loja no mesmo aparelho (link com outro token): nada da
				// anterior fica, nem o carrinho com custo e estoque dela. A rotação
				// da TV é do aparelho e continua.
				const rotation = localStorage.getItem('panel-rotation');
				localStorage.clear();
				if (rotation) localStorage.setItem('panel-rotation', rotation);
				setConnection({} as connectionProps);
			}
			localStorage.setItem('token', token);
			setAuth((prev) => ({ ...prev, token }));
		} else if (token === undefined) {
			signOut();
		}
	};
	function updateAuthId(id: string) {
		const authId = localStorage.getItem('authId');

		if (!authId || authId !== id) {
			localStorage.setItem('authId', id);
		}
	}

	const updateTypeConnection = (
		type: 'multiple' | 'single',
		functions?: connectionProps['functions'],
	) => {
		setConnection((prev) => ({ ...prev, type, functions }));
		if (type === 'multiple') {
			const timeSignIn = localStorage.getItem('timeSignIn');

			if (!timeSignIn) {
				const now = new Date();
				localStorage.setItem('timeSignIn', now.toString());
			}
		}
	};

	function updateBusiness(business: any) {
		setConnection((prev) => ({ ...prev, business }));
	}

	const signOut = () => {
		localStorage.clear();
		setAuth({} as authProps);
		setConnection({} as connectionProps);
		// window.location.reload();
	};

	function verifyTimeLogged() {
		const timeSignIn = localStorage.getItem('timeSignIn');

		if (timeSignIn) {
			const savedDate = new Date(timeSignIn);
			const now = new Date();
			const differenceInMs = now.getTime() - savedDate.getTime();
			const differenceInMinutes = differenceInMs / 1000 / 60;
			const minutesMax = 10;

			if (differenceInMinutes >= minutesMax) {
				signOut();
			}
		}
	}

	useLayoutEffect(() => {
		const urlParams = new URLSearchParams(window.location.search);
		const token = urlParams.get('token');
		if (!token) {
			verifyTimeLogged();
		} else {
			urlParams.delete('token');
			window.history.replaceState(
				{},
				'',
				`${window.location.pathname}?${urlParams.toString()}`,
			);
			updateToken(token || '');
		}
	}, []);

	return (
		<appContext.Provider
			value={{
				updateToken,
				auth,
				signOut,
				updateTypeConnection,
				connection,
				updateBusiness,
				updateAuthId,
				isTV,
			}}
		>
			{children}
		</appContext.Provider>
	);
}
